import {
  Dropdown,
  FluentProvider,
  Option,
  Text,
  makeStyles,
  webLightTheme,
  tokens,
  type OptionOnSelectData,
  type SelectionEvents,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  dropdown: {
    minWidth: '90px',
  },
  empty: {
    display: 'block',
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
});

export interface PanelOption {
  key: string;
  text: string;
}

interface PanelOptionsDropdownProps {
  // `undefined` = still loading; `[]` = loaded but empty.
  options: PanelOption[] | undefined;
  selected: string;
  onSelect: (value: string) => void;
}

// Shared group-by/status dropdown for dashboard panels. Always rendered (so the
// control is visible even with no options) and shows an empty message inside
// the listbox when the backend returns no options.
const PanelOptionsDropdown = ({
  options,
  selected,
  onSelect,
}: PanelOptionsDropdownProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const opts = options ?? [];
  const selectedText =
    opts.find((o) => o.key === selected)?.text ?? opts[0]?.text ?? '';

  return (
    <FluentProvider theme={webLightTheme} style={{ background: 'transparent' }}>
      <Dropdown
        className={styles.dropdown}
        size="small"
        selectedOptions={selected ? [selected] : []}
        value={selectedText}
        placeholder={t('selectOption', 'Select')}
        onOptionSelect={(_e: SelectionEvents, d: OptionOnSelectData) =>
          onSelect(d.optionValue ?? '')
        }
      >
        {opts.length > 0 ? (
          opts.map((o) => (
            <Option key={o.key} value={o.key} text={o.text}>
              {o.text}
            </Option>
          ))
        ) : (
          <Text className={styles.empty} size={200}>
            {t('noOptions', 'No options')}
          </Text>
        )}
      </Dropdown>
    </FluentProvider>
  );
};

export default PanelOptionsDropdown;
